import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNotifications } from "../contexts/NotificationContext";
import { useTheme } from "../contexts/ThemeSelection";
import useAccentColor from "../hooks/useAccentColor";
import { InAppNotification } from "../types";
import styles from "../styles/NotificationBell.module.css";

const typeIcons: Record<InAppNotification["type"], string> = {
  overdue: "\u26a0\ufe0f",
  today: "\ud83d\udcc5",
  reminder: "\u23f0",
  info: "\u2139\ufe0f",
  nudge: "\ud83d\udca1",
  streak: "\ud83d\udd25",
  announcement: "\ud83d\udce3",
  social: "\ud83d\udc65",
};

const timeAgo = (date: Date): string => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

interface NotificationBellProps {
  colorOverride?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ colorOverride }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { activeTheme, theme } = useTheme();
  const { accent } = useAccentColor();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({});

  // Position dropdown smartly — above if near bottom, below otherwise
  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 380; // max-height from CSS

    if (spaceBelow < dropdownHeight) {
      // Not enough room below — open upward, to the right of the bell
      setPos({
        bottom: window.innerHeight - rect.top + 10,
        left: rect.right + 10,
      });
    } else {
      // Enough room below — open downward
      setPos({
        top: rect.bottom + 10,
        right: window.innerWidth - rect.right - 8,
      });
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    updatePos();
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, updatePos]);

  const dropdown = open
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={{
            ...pos,
            backgroundColor: theme[activeTheme].panelColor,
            color: theme[activeTheme].color,
            border: theme[activeTheme].border,
          }}
        >
          <div className={styles.dropdownHeader}>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button
                className={styles.markRead}
                style={{ color: theme[activeTheme].color }}
                onClick={() => markAllAsRead()}
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className={styles.emptyState}>No notifications yet</div>
          ) : (
            notifications.slice(0, 20).map((n) => (
              <button
                key={n.id}
                className={`${styles.notificationItem} ${n.read ? styles.read : styles.unread}`}
                onClick={() => markAsRead(n.id)}
              >
                <span className={styles.itemIcon}>{typeIcons[n.type]}</span>
                <div className={styles.itemContent}>
                  <div className={styles.itemTitle}>{n.title}</div>
                  <div className={styles.itemBody}>{n.body}</div>
                  <div className={styles.itemTime}>{timeAgo(n.timestamp)}</div>
                </div>
              </button>
            ))
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        className={styles.bellButton}
        onClick={() => setOpen((prev) => !prev)}
        style={{ color: colorOverride || accent }}
      >
        <i className="fa-solid fa-bell" style={{ fontSize: "24px" }} />
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>
      {dropdown}
    </>
  );
};

export default NotificationBell;
